/**
 * Every page this person reaches, across their agents (`GET /api/work?kind=page`),
 * newest first. A screen that shows pages asks for a fresh listing when it
 * mounts and shows the last one meanwhile; an edit finishing anywhere asks
 * again, since an edit may rename a page.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { workApi, type PageSummary } from '../services/api';

/** Where a page opens: in the frame, at its id. */
export const pagePath = (pageId: string) => `/pages/${pageId}`;

interface PagesIndexState {
  pages: PageSummary[] | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

// A refresh asked for while one is in flight runs once it lands — that one may have been
// answered before the change (a rename) that asked for this.
let again = false;

export const usePagesIndexStore = create<PagesIndexState>((set, get) => ({
  pages: null,
  error: null,
  loading: false,
  refresh: () => {
    if (get().loading) {
      again = true;
      return;
    }
    set({ loading: true });
    const settle = (next: Partial<PagesIndexState>) => {
      set({ ...next, loading: false });
      if (again) {
        again = false;
        get().refresh();
      }
    };
    workApi.list('page').then(
      (pages) => settle({ pages, error: null }),
      (e) => settle({ error: e instanceof Error ? e.message : 'Failed to load pages' }),
    );
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('page-updated', () => usePagesIndexStore.getState().refresh());
}

/** The pages listing, fetched afresh when the calling screen mounts. */
export function usePagesIndex() {
  const pages = usePagesIndexStore((s) => s.pages);
  const error = usePagesIndexStore((s) => s.error);
  useEffect(() => usePagesIndexStore.getState().refresh(), []);
  return { pages, error };
}
